import { useState, useSyncExternalStore } from "react";
import { shallowEqual } from "react-redux";

// Minimal external store around the data reducer. With useReducer the state
// lived in the DataContext value, so every status change (saving, loading,
// reloadingPreview, diff, ...) re-rendered every consumer of the context:
// with a store, components subscribe only to the slice they select.
export function createDataStore(reducer, initialState) {
  let state = initialState;
  let dispatching = false;
  const queue = [];
  const listeners = new Set();

  const getState = () => state;

  const dispatch = (action) => {
    queue.push(action);
    // Reducer callbacks (an action with a cb, called by the reducer) may
    // dispatch while the reducer is running: queue those actions so none is
    // lost.
    if (dispatching) return;

    dispatching = true;
    const previous = state;
    try {
      while (queue.length > 0) state = reducer(state, queue.shift());
    } catch (error) {
      queue.length = 0;
      throw error;
    } finally {
      dispatching = false;
      if (state !== previous) listeners.forEach((listener) => listener());
    }
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { getState, dispatch, subscribe };
}

// Memoized selection for useSyncExternalStore: returns the previous value when
// state and selector are unchanged, or when the new value is shallow-equal to
// the previous one, so getSnapshot stays stable and components re-render only
// when their selected slice changes.
export function createSelection() {
  let last = null;

  return (state, selector) => {
    if (last && last.state === state && last.selector === selector)
      return last.value;

    const value = selector ? selector(state) : state;
    const stable = last && shallowEqual(last.value, value) ? last.value : value;
    last = { state, selector, value: stable };

    return stable;
  };
}

// Subscribes the component to the store: it re-renders only when the value
// returned by the selector changes (compared shallowly).
export function useStoreSelector(store, selector) {
  const [select] = useState(createSelection);

  return useSyncExternalStore(store.subscribe, () =>
    select(store.getState(), selector)
  );
}
